import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import BEM from 'common/bem';
import attachmentType from 'common/utils/attachmentType';
import Images from 'models/enums/Images';

const classes = BEM.with('ArchivedAttachmentViewer');

// jsdom (used in tests) doesn't implement revokeObjectURL, so guard the call rather than letting
// cleanup/close logic throw in every test that doesn't explicitly mock it.
function safeRevokeObjectURL(url: string) {
  if (typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url);
}

export type ArchivedAttachmentClient = {
  archiving: {
    getMessageAttachment: (
      organizationId: string,
      messageId: string
    ) => Promise<{ blob: Blob; url: string }>;
  };
};

type AttachmentState = {
  messageId: string;
  url: string;
  blob: Blob;
};

/**
 * Triggers a plain browser download for a Blob already fetched — unlike common/utils/downloadFile,
 * which builds a new Blob from a string body, this works directly off the Blob the attachment
 * endpoint already returned, avoiding a pointless re-encode of (possibly large) binary content.
 */
// Revoking the object URL immediately after click() can cancel the download in Safari/Firefox
// and older Chromium for larger blobs, since the browser hasn't necessarily started reading it
// yet — give it a generous delay, and keep the anchor attached to the DOM for the same reason
// (some browsers only reliably dispatch the click on an attached element).
const DOWNLOAD_URL_REVOKE_DELAY_MS = 30000;

function downloadBlob(blob: Blob, filename: string) {
  const anchor = document.createElement('a');
  const url = URL.createObjectURL(blob);
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => safeRevokeObjectURL(url), DOWNLOAD_URL_REVOKE_DELAY_MS);
}

/**
 * Derives a display filename from the attachment's mimeType, mirroring the extension mapping
 * analytics-service uses server-side (AttachmentDecryptionService.BuildFileName) since the
 * archived-attachment schema has no original filename, only messageId + mimeType.
 */
function buildFileName(messageId: string, mimeType: string): string {
  const extensionByMimeType: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'application/pdf': 'pdf',
    'video/mp4': 'mp4',
    'audio/mpeg': 'mp3',
  };
  const extension = extensionByMimeType[mimeType.toLowerCase()] || 'bin';
  return `${messageId}.${extension}`;
}

/**
 * Whether an attachment can be shown inline in the overlay, vs falling back to a direct download.
 * Unlike the live-message MessageAttachmentStore (which decides this from already-known
 * contentType/size on the message model, before ever fetching), archived attachments carry no
 * mimeType/size in search/conversation results — the backend only reveals mimeType via the
 * response's Content-Type once fetched, so this decision can only be made after the fetch
 * completes, not before it starts.
 */
function isInlineRenderable(blob: Blob): boolean {
  const type = attachmentType(blob.type);
  if (type === 'Video') return true;
  if (blob.type === 'application/pdf') return true;
  if (type === 'Image') return blob.size <= Images.MAX_DISPLAY_SIZE_BYTES;
  return false;
}

/**
 * Hook + modal for viewing/downloading a single archived message's attachment. Fetches via
 * client.archiving.getMessageAttachment (analytics-service decrypts server-side, design-docs/
 * de-attachment-open-api-design.md), then either renders it inline (image/video/pdf, within the
 * image size cap) or falls back straight to a browser download for everything else — there's no
 * "can't render this" empty state, per product decision.
 */
export function useArchivedAttachmentViewer(
  client: ArchivedAttachmentClient | null,
  currentOrganizationId: string
) {
  const [attachment, setAttachment] = useState<AttachmentState | null>(null);
  const [loadingMessageId, setLoadingMessageId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Guards against an older view() call's response overwriting a newer one when a user clicks a
  // second attachment before the first fetch resolves — only the request matching the latest
  // token is allowed to update state.
  const requestTokenRef = useRef(0);

  // The SDK lazily creates this object URL per getMessageAttachment call (HttpClient's
  // downloadFileBlobAndUrlFromUrl) and has no lifecycle hook into this viewer, so revoking it is
  // this component's responsibility — otherwise every viewed attachment leaks memory until unload.
  const attachmentUrlRef = useRef<string | null>(null);

  useEffect(
    () => () => {
      // Bump the token so an in-flight view() that resolves after unmount takes the stale-token
      // branch below (which only revokes, never calls setState) instead of writing a fresh
      // attachmentUrlRef/setAttachment on a dead component.
      requestTokenRef.current++;
      if (attachmentUrlRef.current) safeRevokeObjectURL(attachmentUrlRef.current);
    },
    []
  );

  const view = useCallback(
    async (messageId?: string) => {
      if (!client || !messageId) return;

      const requestToken = ++requestTokenRef.current;
      setLoadingMessageId(messageId);
      setError(null);
      try {
        const { blob, url } = await client.archiving.getMessageAttachment(
          currentOrganizationId,
          messageId
        );
        if (requestToken !== requestTokenRef.current) {
          safeRevokeObjectURL(url);
          return;
        }

        if (isInlineRenderable(blob)) {
          if (attachmentUrlRef.current) safeRevokeObjectURL(attachmentUrlRef.current);
          attachmentUrlRef.current = url;
          setAttachment({ messageId, url, blob });
        } else {
          safeRevokeObjectURL(url);
          downloadBlob(blob, buildFileName(messageId, blob.type));
        }
      } catch (err) {
        if (requestToken !== requestTokenRef.current) return;

        // Swallowed into a generic message for the user, but the raw error (which distinguishes
        // "getMessageAttachment doesn't exist yet" / a network failure / a 4xx from
        // analytics-service) is exactly what's needed to diagnose a report of this message —
        // must not be dropped silently.
        Sentry.logger.error(
          Sentry.logger
            .fmt`[ArchivedAttachmentViewer] Failed to fetch attachment for message ${messageId}`,
          { error: err }
        );
        // A failed fetch for a new attachment must not leave a previously-open one visible behind
        // the error banner — the scrim/Escape only dismisses one of them, so show the error alone.
        if (attachmentUrlRef.current) {
          safeRevokeObjectURL(attachmentUrlRef.current);
          attachmentUrlRef.current = null;
        }
        setAttachment(null);
        setError('Something went wrong loading this attachment. Please try again.');
      } finally {
        if (requestToken === requestTokenRef.current) setLoadingMessageId(null);
      }
    },
    [client, currentOrganizationId]
  );

  const close = useCallback(() => {
    if (attachmentUrlRef.current) {
      safeRevokeObjectURL(attachmentUrlRef.current);
      attachmentUrlRef.current = null;
    }
    setAttachment(null);
  }, []);

  // Invalidates any in-flight view() request and clears all visible state (attachment, error,
  // loading) without unmounting the hook. Exposed so a parent can call it when the user navigates
  // away from the view this viewer lives in (e.g. switching search-results tabs) — otherwise a
  // request that was in flight when the user left can still pop open/download after they've moved
  // on, since the hook itself stays mounted across tab switches.
  const reset = useCallback(() => {
    requestTokenRef.current++;
    if (attachmentUrlRef.current) {
      safeRevokeObjectURL(attachmentUrlRef.current);
      attachmentUrlRef.current = null;
    }
    setAttachment(null);
    setError(null);
    setLoadingMessageId(null);
  }, []);

  // A response fetched for a previous organization must never render into the viewer after the
  // user has switched organizations — reset discards it the same way leaving the tab does.
  useEffect(() => {
    reset();
  }, [currentOrganizationId, reset]);

  const download = useCallback(() => {
    if (!attachment) return;
    downloadBlob(attachment.blob, buildFileName(attachment.messageId, attachment.blob.type));
  }, [attachment]);

  const modal = (
    <ArchivedAttachmentModal
      attachment={attachment}
      error={error}
      onClose={close}
      onDownload={download}
      onDismissError={() => setError(null)}
    />
  );

  return { view, modal, isLoading: loadingMessageId, error, reset };
}

type ArchivedAttachmentModalProps = {
  attachment: AttachmentState | null;
  error: string | null;
  onClose: () => void;
  onDownload: () => void;
  onDismissError: () => void;
};

function ArchivedAttachmentModal({
  attachment,
  error,
  onClose,
  onDownload,
  onDismissError,
}: ArchivedAttachmentModalProps) {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const isOpen = !!attachment || !!error;
  const dismiss = attachment ? onClose : onDismissError;

  useEffect(() => {
    if (!isOpen) return;

    closeButtonRef.current?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') dismiss();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run per open/attachment-vs-error switch, not per dismiss identity
  }, [isOpen, !!attachment]);

  if (!isOpen) return null;

  return (
    <div
      aria-label={attachment ? 'Archived attachment' : 'Attachment error'}
      aria-modal="true"
      className={classes()}
      data-test-id="archived-attachment-viewer"
      role="dialog"
    >
      <div aria-hidden="true" className={classes('scrim')} onClick={dismiss} />
      <div className={classes('container')}>
        {error && (
          <div
            className={classes('error')}
            data-test-id="archived-attachment-viewer-error"
            role="alert"
          >
            {error}
            <button
              className={classes('close-button')}
              data-test-id="archived-attachment-viewer-dismiss-error"
              onClick={onDismissError}
              ref={!attachment ? closeButtonRef : undefined}
              type="button"
            >
              Close
            </button>
          </div>
        )}
        {attachment && (
          <>
            <div className={classes('header')}>
              <button
                className={classes('download-action')}
                data-test-id="archived-attachment-viewer-download"
                onClick={onDownload}
                type="button"
              >
                Download
              </button>
              <button
                aria-label="Close attachment"
                className={classes('close-button')}
                data-test-id="archived-attachment-viewer-close"
                onClick={onClose}
                ref={closeButtonRef}
                type="button"
              >
                ×
              </button>
            </div>
            <div className={classes('body')}>{renderAttachment(attachment)}</div>
          </>
        )}
      </div>
    </div>
  );
}

function renderAttachment({ url, blob, messageId }: AttachmentState) {
  const type = attachmentType(blob.type);

  if (blob.type === 'application/pdf') {
    return (
      <iframe
        className={classes('pdf')}
        src={`${url}#view=FitBH`}
        title={`archived-attachment-${messageId}`}
      />
    );
  }
  if (type === 'Video') {
    return (
      <video
        className={classes('video')}
        controls
        data-test-id={`archived-attachment-video-${messageId}`}
        preload="metadata"
        src={url}
      >
        {"Sorry, your browser doesn't support embedded videos."}
      </video>
    );
  }
  return <img alt={`Attachment for message ${messageId}`} className={classes('image')} src={url} />;
}
